import { isLoading } from "apollo/utils";
import { useEnvContext } from "context/EnvContext";
import { APP_CLINIC, REDIRECT_PARAM } from "core/consts";
import { activateBroaderFormMigration } from "core/model/utils/featureFlags";
import {
  getQueryBooleanVariable,
  getQueryVariable,
} from "core/model/utils/urls";
import { initWidgetAuthFlow, isWidgetAuthFlow } from "core/model/utils/widget";
import { useManualSetLegalDocuments } from "dsl/atoms/LegalDocuments";
import { useHandleLogin } from "dsl/ecosystems/CareproviderOnboardingLoginPage/useHandleLogin";
import { LoginPageV2 } from "dsl/ecosystems/LoginPageV2";
import {
  loginPageModelDefinition,
  validateChallenge,
  validateLogin,
  validateSingleSignOn,
} from "dsl/ecosystems/LoginPageV2/formConfig";
import { useCareseekerHandlerUrls } from "dsl/hooks/useNavigationHandlers";
import { useEffect } from "react";
import { SimpleFormRenderProps } from "react-forms-state/src";
import { Navigate, useLocation } from "react-router-dom";
import { useIsLoggedOnClinicApp } from "reduxentities/hooks";
import { useSingleSignOn } from "../CareproviderOnboardingLoginPage/useSingleSingOn";
import { LoginLoadingPage } from "../LoginPageV2/LoginLoadingPage";
import { SenderLoginForm } from "./SenderLoginForm/SenderLoginForm";

export function CareseekerAuthPage() {
  const location = useLocation();
  const { env } = useEnvContext();
  const logged = useIsLoggedOnClinicApp();

  // Persist ?widget=true in sessionStorage so it survives SSO redirect chain
  useEffect(() => {
    initWidgetAuthFlow();
  }, []);

  // Detect widget flow from URL param (first render) or sessionStorage (after SSO redirect)
  const isWidget =
    getQueryBooleanVariable(location.search, "widget") || isWidgetAuthFlow();

  const {
    challengeTimestamp,
    handleLogin,
    isLogin,
    loginErrors,
    loginView,
    progressLogin,
    resetError,
    setLoginView,
    setProgressLogin,
  } = useHandleLogin();
  const {
    checkSsoEligibility,
    handleSsoLogin,
    isSsoEligible,
    isSsoRedirect,
    progressSso,
    progressValidateEmail,
    ssoToken,
    ssoUrl,
  } = useSingleSignOn({ handleLogin });

  const {
    goToContactUs,
    goToForgotPassword,
    goToProductionEnv,
    goToProviderApp,
  } = useCareseekerHandlerUrls();

  // Setting the legal documents to default on the login screen, otherwise
  // it tries to fetch with the id (logout/onboarding), although logged out
  useManualSetLegalDocuments();

  // Skip auto-redirect for widget: user needs to re-authenticate to get a fresh JWT
  if (logged === "ok" && !isWidget) {
    return (
      <Navigate
        to={getQueryVariable(location.search, REDIRECT_PARAM) || "/app"}
      />
    );
  }

  if (isSsoRedirect && isLoading(progressSso)) {
    return (
      <main>
        <LoginLoadingPage app={APP_CLINIC} env={env} />
      </main>
    );
  }

  if (activateBroaderFormMigration) {
    return (
      <main>
        <SenderLoginForm
          app={APP_CLINIC}
          challengeTimestamp={challengeTimestamp}
          contactUsUrl={goToContactUs()}
          env={env}
          goToForgotPassword={goToForgotPassword()}
          goToProductionUrl={goToProductionEnv()}
          isEmailInputLoading={isLoading(progressValidateEmail)}
          isLoading={isLoading([progressLogin, progressSso])}
          isSingleSignOn={isSsoEligible}
          loginErrors={loginErrors}
          loginView={loginView}
          otherAppUrl={goToProviderApp()}
          resetError={resetError}
          ssoToken={ssoToken}
          onBackToLogin={() => {
            resetError();
            setLoginView("login");
          }}
          onChangeEmail={(email) => checkSsoEligibility({ email })}
          onSubmit={(payload) =>
            ssoUrl ? handleSsoLogin() : handleLogin(payload, setProgressLogin)
          }
        />
      </main>
    );
  }

  return (
    <main>
      <SimpleFormRenderProps
        asHtmlForm
        modelDefinition={loginPageModelDefinition}
        validate={
          isLogin
            ? ssoUrl
              ? validateSingleSignOn
              : validateLogin
            : validateChallenge
        }
        onSubmit={(props) =>
          ssoUrl
            ? handleSsoLogin()
            : handleLogin({ ...props, ssoToken }, setProgressLogin)
        }
      >
        {({ onChange, submit }) => (
          <LoginPageV2
            app={APP_CLINIC}
            challengeTimestamp={challengeTimestamp}
            contactUsUrl={goToContactUs()}
            env={env}
            goToForgotPassword={goToForgotPassword()}
            goToProductionUrl={goToProductionEnv()}
            isEmailInputLoading={isLoading(progressValidateEmail)}
            isLoading={isLoading([progressLogin, progressSso])}
            isSingleSignOn={isSsoEligible}
            loginErrors={loginErrors}
            loginView={loginView}
            otherAppUrl={goToProviderApp()}
            resetError={resetError}
            onBackToLogin={() => {
              onChange(null, "challenge", true);
              onChange(null, "email", true);
              onChange(null, "password", true);
              resetError();
              setLoginView("login");
            }}
            onChangeEmail={(email) => checkSsoEligibility({ email })}
            onSubmit={submit}
          />
        )}
      </SimpleFormRenderProps>
    </main>
  );
}
